import React, { useEffect, useState, memo } from 'react';
import ProgressBar from '../components/ProgressBar';
import ActionCard from '../components/ActionCard';
import { HeartIcon, UserIcon, MessageSquareIcon, AlertTriangleIcon, CheckCircleIcon, BookOpenIcon, BellIcon, TrendingUpIcon, StarIcon, ShieldIcon, AwardIcon, BrainIcon, SunIcon, CalendarIcon } from 'lucide-react';
const Dashboard = () => {
  // En una aplicación real, esto vendría del almacenamiento de datos del usuario
  const [currentDay, setCurrentDay] = useState(5);
  const totalDays = 21;
  const [userName, setUserName] = useState('');
  const [userProgress, setUserProgress] = useState({
    diagnosticCompleted: false,
    maleTypeIdentified: false,
    messagesUsed: 2,
    responseRate: 75
  });
  const [selectedTab, setSelectedTab] = useState('resumen');
  // Acciones próximas de ejemplo
  const nextActions = [{
    title: 'Completa tu diagnóstico',
    description: 'Descubre tu tasa de éxito y recibe un plan personalizado para reconquistarlo',
    icon: <HeartIcon size={20} data-id="element-276" />,
    to: '/diagnostic-test',
    priority: 'Alta',
    timeEstimate: '10 minutos'
  }, {
    title: 'Identifica su tipo masculino',
    description: 'Descubre si él es Cazador, Protector o Conquistador para personalizar tu estrategia',
    icon: <UserIcon size={20} data-id="element-277" />,
    to: '/male-type',
    priority: 'Alta',
    timeEstimate: '8 minutos'
  }, {
    title: 'Envía el mensaje del día',
    description: 'Disparador de Nostalgia #2 - Especialmente efectivo para reconectar hoy',
    icon: <MessageSquareIcon size={20} data-id="element-278" />,
    to: '/messages',
    priority: 'Urgente',
    timeEstimate: '5 minutos'
  }, {
    title: 'Protocolo de Emergencia',
    description: '¿No responde o está resistente? Accede a estrategias avanzadas de reconexión',
    icon: <AlertTriangleIcon size={20} data-id="element-279" />,
    to: '/emergency',
    color: 'bg-gradient-to-br from-orange-500 to-red-600',
    priority: 'Según necesidad',
    timeEstimate: '15 minutos'
  }];
  const dailyTips = [{
    id: 1,
    tip: 'La escasez es uno de los disparadores más poderosos. Hoy, déjalo esperando un poco más por tu respuesta. Esto aumentará tu valor percibido y activará su miedo a perderte, especialmente efectivo con hombres tipo Cazador.',
    category: 'Escasez'
  }, {
    id: 2,
    tip: "Cuando respondas sus mensajes, deja una pregunta abierta al final. Esto crea un 'bucle abierto' psicológico que lo mantendrá pensando en ti y ansioso por responder. La curiosidad es un poderoso motivador masculino.",
    category: 'Comunicación'
  }, {
    id: 3,
    tip: "Hoy es ideal para usar la técnica del 'recuerdo indirecto': menciona casualmente algo positivo que vivieron juntos sin expresar nostalgia directamente. Los hombres responden mejor a la nostalgia cuando no sienten presión emocional.",
    category: 'Nostalgia'
  }, {
    id: 4,
    tip: 'Muéstrate vulnerable pero fuerte. Comparte un desafío que estás enfrentando y cómo lo estás superando. Esto activa el instinto protector masculino sin parecer necesitada o dependiente.',
    category: 'Vulnerabilidad Estratégica'
  }, {
    id: 5,
    tip: "Utiliza el principio de 'reciprocidad': cuando él comparta algo personal, responde con algo de valor similar. Esto crea una escalada natural de intimidad sin presionarlo.",
    category: 'Conexión Emocional'
  }];
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [recentActivities, setRecentActivities] = useState([{
    id: 1,
    type: 'message',
    description: 'Enviaste el Disparador de Nostalgia #1',
    time: 'Ayer, 19:45',
    result: 'Visto, sin respuesta',
    advice: 'Mantén la calma. El silencio también es una respuesta y puede indicar que está procesando emociones. Espera 48h antes del siguiente mensaje.'
  }, {
    id: 2,
    type: 'test',
    description: 'Completaste el Test de Diagnóstico',
    time: 'Hace 2 días',
    result: '85% probabilidad de éxito',
    advice: 'Tu situación es favorable. Enfócate en los disparadores de Nostalgia y Escasez en esta fase inicial.'
  }, {
    id: 3,
    type: 'male-type',
    description: 'Identificaste su tipo como Cazador',
    time: 'Hace 2 días',
    result: 'Estrategia personalizada activada',
    advice: 'Los hombres Cazador responden mejor a la independencia y el misterio. Evita mostrarte demasiado disponible.'
  }]);
  const [insights, setInsights] = useState([{
    id: 1,
    title: 'Patrón de Respuesta',
    description: 'Él responde más rápido por la noche (después de las 20h). Programa tus mensajes en este horario para mayor impacto.',
    icon: <BellIcon size={18} className="text-purple-500" data-id="element-280" />,
    action: 'Envía mensajes importantes entre 20:00-22:00h'
  }, {
    id: 2,
    title: 'Tema de Interés',
    description: 'Muestra mayor engagement cuando mencionas viajes o aventuras. Utiliza estas temáticas para crear conexión sin parecer forzada.',
    icon: <TrendingUpIcon size={18} className="text-blue-500" data-id="element-281" />,
    action: 'Incorpora referencias sutiles a aventuras compartidas o futuras'
  }, {
    id: 3,
    title: 'Señal de Progreso',
    description: 'Ha aumentado la longitud de sus mensajes en un 40% esta semana. Esto indica creciente interés y apertura emocional.',
    icon: <CheckCircleIcon size={18} className="text-green-500" data-id="element-282" />,
    action: 'Responde con mensajes ligeramente más largos pero mantén algo de misterio'
  }, {
    id: 4,
    title: 'Punto de Resistencia',
    description: 'Evita hablar sobre compromisos futuros por ahora. Sus respuestas se vuelven más cortas cuando siente presión sobre "hacia dónde va esto".',
    icon: <ShieldIcon size={18} className="text-orange-500" data-id="element-283" />,
    action: 'Mantén en el presente, enfocada en reconexión sin expectativas explícitas'
  }]);
  const [psychologyInsights, setPsychologyInsights] = useState([{
    id: 1,
    title: 'Principio de Escasez',
    description: 'Los hombres valoran más lo que perciben como difícil de obtener. Tu disponibilidad limitada aumenta tu valor percibido.',
    icon: <StarIcon size={18} className="text-amber-500" data-id="element-284" />,
    technique: 'Técnica 80/20: Por cada 5 mensajes que él envíe, responde a 4. Mantén un patrón ligeramente impredecible.'
  }, {
    id: 2,
    title: 'Disonancia Cognitiva',
    description: 'Cuando sus acciones (buscarte) no coinciden con sus creencias ("no debería interesarme"), su mente crea justificaciones para resolver el conflicto.',
    icon: <BrainIcon size={18} className="text-indigo-500" data-id="element-285" />,
    technique: 'Dale razones socialmente aceptables para buscarte que no amenacen su ego o decisión anterior de separarse.'
  }, {
    id: 3,
    title: 'Efecto Zeigarnik',
    description: 'La mente masculina está programada para recordar y obsesionarse con tareas incompletas o situaciones sin resolver.',
    icon: <SunIcon size={18} className="text-orange-500" data-id="element-286" />,
    technique: 'Crea "bucles abiertos" en conversaciones - historias sin terminar, preguntas sin responder, misterios sutiles.'
  }, {
    id: 4,
    title: 'Reciprocidad Emocional',
    description: 'Los hombres se sienten obligados a corresponder cuando reciben algo de valor, incluyendo vulnerabilidad auténtica y reconocimiento.',
    icon: <AwardIcon size={18} className="text-emerald-500" data-id="element-287" />,
    technique: 'Ofrece validación genuina sobre sus fortalezas y reconoce su impacto positivo en tu vida sin parecer necesitada.'
  }]);
  // Ciclo a través de consejos cada 15 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex(prevIndex => prevIndex === dailyTips.length - 1 ? 0 : prevIndex + 1);
    }, 15000);
    return () => clearInterval(interval);
  }, [dailyTips.length]);
  const renderTabContent = () => {
    switch (selectedTab) {
      case 'resumen':
        return <>
            <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-288">
              <h2 className="text-xl font-semibold text-[#E91E63] mb-4" data-id="element-289">
                Progreso del Viaje
              </h2>
              <ProgressBar currentDay={currentDay} totalDays={totalDays} label="21 Días de Reconquista" data-id="element-290" />
              <div className="mt-6 flex flex-wrap justify-around text-center" data-id="element-291">
                <div className="px-4 py-2" data-id="element-292">
                  <div className="text-3xl font-bold text-[#9C27B0]" data-id="element-293">
                    {currentDay}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-294">Días Completados</div>
                </div>
                <div className="px-4 py-2" data-id="element-295">
                  <div className="text-3xl font-bold text-[#2196F3]" data-id="element-296">
                    {totalDays - currentDay}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-297">Días Restantes</div>
                </div>
                <div className="px-4 py-2" data-id="element-298">
                  <div className="text-3xl font-bold text-[#FFD700]" data-id="element-299">85%</div>
                  <div className="text-sm text-gray-500" data-id="element-300">Tasa de Éxito</div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-301">
                <h3 className="font-semibold text-[#2C2C2C] mb-3" data-id="element-302">
                  Estado de Reconquista
                </h3>
                <div className="bg-purple-50 rounded-lg p-4" data-id="element-303">
                  <div className="flex items-start" data-id="element-304">
                    <div className="mr-3 mt-1" data-id="element-305">
                      <TrendingUpIcon size={20} className="text-purple-600" data-id="element-306" />
                    </div>
                    <div data-id="element-307">
                      <p className="text-sm text-gray-700" data-id="element-308">
                        <span className="font-medium" data-id="element-309">Fase actual:</span>{' '}
                        Despertar Interés (Días 4-7)
                      </p>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-310">
                        En esta fase, tu objetivo es crear curiosidad y
                        reestablecer una conexión emocional positiva con él.
                        Estás progresando bien, con señales de interés creciente
                        por su parte. Continúa alternando entre disparadores de
                        nostalgia y escasez para maximizar resultados.
                      </p>
                      <div className="mt-3" data-id="element-311">
                        <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-312">
                          <div className="bg-purple-600 h-1.5 rounded-full" style={{
                          width: '65%'
                        }} data-id="element-313"></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-314">
                          <span data-id="element-315">Fase 2/5</span>
                          <span data-id="element-316">65% completada</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-8" data-id="element-317">
              <h2 className="text-xl font-semibold text-[#2C2C2C] mb-4" data-id="element-318">
                Próximas Acciones
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-319">
                {nextActions.map((action, index) => <ActionCard key={index} title={action.title} description={action.description} icon={action.icon} to={action.to} color={action.color} data-id="element-320" />)}
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-321">
              <div className="flex items-center justify-between mb-4" data-id="element-322">
                <h2 className="text-xl font-semibold text-[#2C2C2C]" data-id="element-323">
                  Consejo del Día
                </h2>
                <span className={`text-xs px-2 py-1 rounded-full ${dailyTips[currentTipIndex].category === 'Escasez' ? 'bg-orange-100 text-orange-700' : dailyTips[currentTipIndex].category === 'Nostalgia' ? 'bg-pink-100 text-pink-700' : dailyTips[currentTipIndex].category === 'Vulnerabilidad Estratégica' ? 'bg-red-100 text-red-700' : dailyTips[currentTipIndex].category === 'Conexión Emocional' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-324">
                  {dailyTips[currentTipIndex].category}
                </span>
              </div>
              <div className="bg-gray-50 border-l-4 border-[#FFD700] p-4" data-id="element-325">
                <p className="text-gray-700" data-id="element-326">
                  <span className="font-bold" data-id="element-327">Recuerda:</span>{' '}
                  {dailyTips[currentTipIndex].tip}
                </p>
              </div>
              <div className="mt-3 flex justify-center" data-id="element-328">
                <div className="flex space-x-1" data-id="element-329">
                  {dailyTips.map((_, idx) => <button key={idx} onClick={() => setCurrentTipIndex(idx)} className={`w-2 h-2 rounded-full ${idx === currentTipIndex ? 'bg-[#E91E63]' : 'bg-gray-300'}`} data-id="element-330" />)}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6 mb-8" data-id="element-331">
              <h2 className="text-xl font-semibold text-[#2C2C2C] mb-4" data-id="element-332">
                Psicología Masculina: Claves del Día
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-333">
                {psychologyInsights.slice(0, 2).map(insight => <div key={insight.id} className="border rounded-lg p-4" data-id="element-334">
                    <div className="flex items-start mb-3" data-id="element-335">
                      <div className="mr-3 p-2 rounded-full bg-gray-100" data-id="element-336">
                        {insight.icon}
                      </div>
                      <div data-id="element-337">
                        <h3 className="font-medium text-[#2C2C2C]" data-id="element-338">
                          {insight.title}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1" data-id="element-339">
                          {insight.description}
                        </p>
                      </div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-md" data-id="element-340">
                      <p className="text-sm text-blue-800" data-id="element-341">
                        <span className="font-medium" data-id="element-342">
                          Aplicación práctica:
                        </span>{' '}
                        {insight.technique}
                      </p>
                    </div>
                  </div>)}
              </div>
              <div className="mt-4 text-center" data-id="element-343">
                <a href="#" className="text-sm font-medium text-[#E91E63] hover:underline" data-id="element-344">
                  Ver más principios de psicología masculina →
                </a>
              </div>
            </div>
          </>;
      case 'insights':
        return <div className="bg-white rounded-xl shadow-md p-6" data-id="element-345">
            <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6" data-id="element-346">
              Análisis y Patrones Detectados
            </h2>
            <div className="mb-8" data-id="element-347">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-348">
                Insights Personalizados
              </h3>
              <div className="space-y-4" data-id="element-349">
                {insights.map(insight => <div key={insight.id} className="flex items-start bg-gray-50 p-4 rounded-lg" data-id="element-350">
                    <div className="mr-3 mt-1" data-id="element-351">{insight.icon}</div>
                    <div data-id="element-352">
                      <h4 className="font-medium text-[#2C2C2C]" data-id="element-353">
                        {insight.title}
                      </h4>
                      <p className="text-sm text-gray-700" data-id="element-354">
                        {insight.description}
                      </p>
                      <div className="mt-2 bg-blue-50 p-2 rounded" data-id="element-355">
                        <p className="text-xs text-blue-800" data-id="element-356">
                          <span className="font-medium" data-id="element-357">
                            Acción recomendada:
                          </span>{' '}
                          {insight.action}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="mb-8" data-id="element-358">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-359">
                Análisis de Comunicación
              </h3>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-360">
                <div className="mb-4" data-id="element-361">
                  <div className="flex justify-between mb-1" data-id="element-362">
                    <span className="text-sm text-gray-700" data-id="element-363">
                      Tasa de respuesta
                    </span>
                    <span className="text-sm font-medium text-[#2C2C2C]" data-id="element-364">
                      75%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-365">
                    <div className="bg-green-500 h-2 rounded-full" style={{
                    width: '75%'
                  }} data-id="element-366"></div>
                  </div>
                </div>
                <div className="mb-4" data-id="element-367">
                  <div className="flex justify-between mb-1" data-id="element-368">
                    <span className="text-sm text-gray-700" data-id="element-369">
                      Tiempo promedio de respuesta
                    </span>
                    <span className="text-sm font-medium text-[#2C2C2C]" data-id="element-370">
                      2.5 horas
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-371">
                    <div className="bg-blue-500 h-2 rounded-full" style={{
                    width: '60%'
                  }} data-id="element-372"></div>
                  </div>
                </div>
                <div data-id="element-373">
                  <div className="flex justify-between mb-1" data-id="element-374">
                    <span className="text-sm text-gray-700" data-id="element-375">
                      Engagement emocional
                    </span>
                    <span className="text-sm font-medium text-[#2C2C2C]" data-id="element-376">
                      Medio-Alto
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-377">
                    <div className="bg-purple-500 h-2 rounded-full" style={{
                    width: '70%'
                  }} data-id="element-378"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-8" data-id="element-379">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-380">
                Patrones Masculinos Identificados
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-381">
                <div className="bg-blue-50 p-4 rounded-lg" data-id="element-382">
                  <h4 className="font-medium text-blue-800 mb-2" data-id="element-383">
                    Patrones de Comunicación
                  </h4>
                  <ul className="space-y-2 text-sm" data-id="element-384">
                    <li className="flex items-start" data-id="element-385">
                      <div className="bg-blue-200 text-blue-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-386">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-387"></span>
                      </div>
                      <span data-id="element-388">
                        Responde más extensamente cuando no se siente presionado
                        emocionalmente
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-389">
                      <div className="bg-blue-200 text-blue-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-390">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-391"></span>
                      </div>
                      <span data-id="element-392">
                        Utiliza más emojis cuando está de buen humor y receptivo
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-393">
                      <div className="bg-blue-200 text-blue-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-394">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-395"></span>
                      </div>
                      <span data-id="element-396">
                        Inicia conversación principalmente por las noches
                        (después de su jornada)
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg" data-id="element-397">
                  <h4 className="font-medium text-purple-800 mb-2" data-id="element-398">
                    Disparadores Emocionales Efectivos
                  </h4>
                  <ul className="space-y-2 text-sm" data-id="element-399">
                    <li className="flex items-start" data-id="element-400">
                      <div className="bg-purple-200 text-purple-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-401">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-402"></span>
                      </div>
                      <span data-id="element-403">
                        Referencias a momentos de logro compartido (viaje a
                        montaña)
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-404">
                      <div className="bg-purple-200 text-purple-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-405">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-406"></span>
                      </div>
                      <span data-id="element-407">
                        Menciones de tu admiración por su capacidad de resolver
                        problemas
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-408">
                      <div className="bg-purple-200 text-purple-700 p-1 rounded-full mr-2 mt-0.5" data-id="element-409">
                        <span className="block h-1.5 w-1.5 rounded-full bg-current" data-id="element-410"></span>
                      </div>
                      <span data-id="element-411">
                        Comentarios sobre actividades físicas/deportivas que
                        disfruta
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div data-id="element-412">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-413">
                Recomendaciones Basadas en Datos
              </h3>
              <ul className="space-y-3" data-id="element-414">
                <li className="flex items-start bg-blue-50 p-3 rounded-md" data-id="element-415">
                  <CheckCircleIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-416" />
                  <span className="text-sm" data-id="element-417">
                    Aumenta el uso de disparadores de nostalgia - han generado
                    30% más respuestas. Enfócate en momentos donde él se sintió
                    competente y valorado.
                  </span>
                </li>
                <li className="flex items-start bg-blue-50 p-3 rounded-md" data-id="element-418">
                  <CheckCircleIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-419" />
                  <span className="text-sm" data-id="element-420">
                    Envía mensajes entre 20:00-22:00h para mayor probabilidad de
                    respuesta inmediata. Su receptividad emocional aumenta un
                    40% en este horario.
                  </span>
                </li>
                <li className="flex items-start bg-blue-50 p-3 rounded-md" data-id="element-421">
                  <CheckCircleIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-422" />
                  <span className="text-sm" data-id="element-423">
                    Incorpora más referencias a experiencias compartidas al aire
                    libre. Los recuerdos asociados a adrenalina y aventura
                    activan su sistema de recompensa cerebral.
                  </span>
                </li>
                <li className="flex items-start bg-blue-50 p-3 rounded-md" data-id="element-424">
                  <CheckCircleIcon size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-425" />
                  <span className="text-sm" data-id="element-426">
                    Reduce la frecuencia de preguntas directas sobre sus
                    sentimientos. Los hombres tipo Cazador prefieren expresarse
                    a través de acciones, no palabras.
                  </span>
                </li>
              </ul>
            </div>
          </div>;
      case 'actividad':
        return <div className="bg-white rounded-xl shadow-md p-6" data-id="element-427">
            <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6" data-id="element-428">
              Registro de Actividades
            </h2>
            <div className="mb-8" data-id="element-429">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-430">
                Actividades Recientes
              </h3>
              <div className="space-y-4" data-id="element-431">
                {recentActivities.map(activity => <div key={activity.id} className="border-b border-gray-100 pb-4" data-id="element-432">
                    <div className="flex justify-between items-start" data-id="element-433">
                      <div className="flex items-start" data-id="element-434">
                        <div className="p-2 rounded-full bg-gray-100 mr-3" data-id="element-435">
                          {activity.type === 'message' ? <MessageSquareIcon size={16} className="text-pink-500" data-id="element-436" /> : activity.type === 'test' ? <HeartIcon size={16} className="text-purple-500" data-id="element-437" /> : <UserIcon size={16} className="text-blue-500" data-id="element-438" />}
                        </div>
                        <div data-id="element-439">
                          <p className="font-medium text-[#2C2C2C]" data-id="element-440">
                            {activity.description}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-441">
                            {activity.time}
                          </p>
                        </div>
                      </div>
                      <div className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700" data-id="element-442">
                        {activity.result}
                      </div>
                    </div>
                    <div className="mt-3 ml-11" data-id="element-443">
                      <div className="text-xs bg-pink-50 text-pink-800 p-2 rounded" data-id="element-444">
                        <span className="font-medium" data-id="element-445">Consejo de experta:</span>{' '}
                        {activity.advice}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            <div className="mb-8" data-id="element-446">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-447">
                Calendario de Mensajes
              </h3>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-448">
                <div className="flex items-start mb-4" data-id="element-449">
                  <div className="p-2 rounded-full bg-pink-100 mr-3" data-id="element-450">
                    <CalendarIcon size={16} className="text-pink-600" data-id="element-451" />
                  </div>
                  <div data-id="element-452">
                    <h4 className="font-medium text-[#2C2C2C]" data-id="element-453">
                      Próximos 3 días - Plan de Mensajes
                    </h4>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-454">
                      Secuencia estratégica para maximizar respuestas
                    </p>
                  </div>
                </div>
                <div className="space-y-3" data-id="element-455">
                  <div className="flex items-center border-l-2 border-green-500 pl-3 py-1" data-id="element-456">
                    <div className="w-12 text-xs font-medium text-gray-500" data-id="element-457">
                      Hoy
                    </div>
                    <div className="flex-1 ml-2" data-id="element-458">
                      <p className="text-sm font-medium" data-id="element-459">
                        Disparador de Nostalgia #2
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-460">
                        Mejor horario: 20:00-21:00h
                      </p>
                    </div>
                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded" data-id="element-461">
                      Pendiente
                    </div>
                  </div>
                  <div className="flex items-center border-l-2 border-gray-300 pl-3 py-1" data-id="element-462">
                    <div className="w-12 text-xs font-medium text-gray-500" data-id="element-463">
                      Mañana
                    </div>
                    <div className="flex-1 ml-2" data-id="element-464">
                      <p className="text-sm font-medium" data-id="element-465">
                        Disparador de Escasez #1
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-466">
                        Mejor horario: 10:00-12:00h
                      </p>
                    </div>
                    <div className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded" data-id="element-467">
                      Programado
                    </div>
                  </div>
                  <div className="flex items-center border-l-2 border-gray-300 pl-3 py-1" data-id="element-468">
                    <div className="w-12 text-xs font-medium text-gray-500" data-id="element-469">
                      Día 7
                    </div>
                    <div className="flex-1 ml-2" data-id="element-470">
                      <p className="text-sm font-medium" data-id="element-471">
                        Disparador de Misterio #2
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-472">
                        Mejor horario: 19:00-22:00h
                      </p>
                    </div>
                    <div className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded" data-id="element-473">
                      Programado
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-center" data-id="element-474">
                  <a href="/calendar" className="text-xs font-medium text-pink-600 hover:text-pink-800" data-id="element-475">
                    Ver calendario completo de 21 días →
                  </a>
                </div>
              </div>
            </div>
            <div data-id="element-476">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-477">
                Resumen de Progreso
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-478">
                <div className="bg-gray-50 p-4 rounded-lg text-center" data-id="element-479">
                  <div className="text-2xl font-bold text-[#E91E63]" data-id="element-480">
                    {userProgress.messagesUsed}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-481">Mensajes Enviados</div>
                  <div className="mt-2 text-xs text-gray-500" data-id="element-482">
                    Efectividad: 85%
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center" data-id="element-483">
                  <div className="text-2xl font-bold text-[#E91E63]" data-id="element-484">
                    {userProgress.responseRate}%
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-485">Tasa de Respuesta</div>
                  <div className="mt-2 text-xs text-gray-500" data-id="element-486">
                    ↑15% vs semana anterior
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center" data-id="element-487">
                  <div className="text-2xl font-bold text-[#E91E63]" data-id="element-488">7</div>
                  <div className="text-sm text-gray-600" data-id="element-489">
                    Días de Seguimiento
                  </div>
                  <div className="mt-2 text-xs text-gray-500" data-id="element-490">
                    33% del programa completado
                  </div>
                </div>
              </div>
              <div className="mt-5 bg-blue-50 p-4 rounded-lg" data-id="element-491">
                <h4 className="font-medium text-blue-800 mb-2 text-sm" data-id="element-492">
                  Análisis de Receptividad
                </h4>
                <p className="text-xs text-gray-700 mb-3" data-id="element-493">
                  Basado en los patrones de respuesta de los últimos 7 días
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-494">
                  <div data-id="element-495">
                    <div className="text-xs text-gray-600 mb-1" data-id="element-496">
                      Mensajes más efectivos
                    </div>
                    <div className="flex items-center" data-id="element-497">
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-498"></div>
                      <span className="text-xs" data-id="element-499">Nostalgia (85% respuesta)</span>
                    </div>
                    <div className="flex items-center mt-1" data-id="element-500">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-501"></div>
                      <span className="text-xs" data-id="element-502">Misterio (75% respuesta)</span>
                    </div>
                  </div>
                  <div data-id="element-503">
                    <div className="text-xs text-gray-600 mb-1" data-id="element-504">
                      Mensajes menos efectivos
                    </div>
                    <div className="flex items-center" data-id="element-505">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-506"></div>
                      <span className="text-xs" data-id="element-507">Futuro (40% respuesta)</span>
                    </div>
                    <div className="flex items-center mt-1" data-id="element-508">
                      <div className="w-3 h-3 rounded-full bg-orange-500 mr-2" data-id="element-509"></div>
                      <span className="text-xs" data-id="element-510">
                        Vulnerabilidad (55% respuesta)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'plan':
        return <div className="bg-white rounded-xl shadow-md p-6" data-id="element-511">
            <h2 className="text-xl font-semibold text-[#2C2C2C] mb-6" data-id="element-512">
              Plan Personalizado
            </h2>
            <div className="mb-8" data-id="element-513">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-514">
                Plan de 21 Días para Reconquistarlo
              </h3>
              <div className="relative" data-id="element-515">
                <div className="absolute left-4 inset-y-0 w-0.5 bg-gray-200" data-id="element-516"></div>
                <div className="space-y-6" data-id="element-517">
                  <div className="relative pl-12" data-id="element-518">
                    <div className="absolute left-2 -translate-x-1/2 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs" data-id="element-519">
                      1
                    </div>
                    <div data-id="element-520">
                      <h4 className="font-medium text-[#2C2C2C]" data-id="element-521">
                        Fase 1: Reconexión (Días 1-3)
                      </h4>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-522">
                        Reestablece contacto de forma casual y sin presión
                        emocional. Utiliza disparadores de valor y misterio para
                        despertar su curiosidad sin parecer necesitada.
                      </p>
                      <div className="mt-2 flex items-center" data-id="element-523">
                        <div className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700" data-id="element-524">
                          Completado
                        </div>
                      </div>
                      <div className="mt-3 bg-gray-50 p-3 rounded-md" data-id="element-525">
                        <h5 className="text-xs font-medium text-[#2C2C2C] mb-2" data-id="element-526">
                          Logros clave de esta fase:
                        </h5>
                        <ul className="text-xs text-gray-700 space-y-1" data-id="element-527">
                          <li className="flex items-start" data-id="element-528">
                            <CheckCircleIcon size={12} className="text-green-500 mr-1 mt-0.5" data-id="element-529" />
                            <span data-id="element-530">
                              Primer contacto establecido sin resistencia
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-531">
                            <CheckCircleIcon size={12} className="text-green-500 mr-1 mt-0.5" data-id="element-532" />
                            <span data-id="element-533">
                              Respondió positivamente al disparador de valor
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-534">
                            <CheckCircleIcon size={12} className="text-green-500 mr-1 mt-0.5" data-id="element-535" />
                            <span data-id="element-536">
                              Mostró curiosidad sobre tu situación actual
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-12" data-id="element-537">
                    <div className="absolute left-2 -translate-x-1/2 w-6 h-6 rounded-full bg-[#E91E63] text-white flex items-center justify-center text-xs" data-id="element-538">
                      2
                    </div>
                    <div data-id="element-539">
                      <h4 className="font-medium text-[#2C2C2C]" data-id="element-540">
                        Fase 2: Despertar Interés (Días 4-7)
                      </h4>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-541">
                        Crea momentos de nostalgia y utiliza técnicas de escasez
                        para aumentar tu valor percibido. Establece comunicación
                        intermitente pero significativa que active su deseo de
                        buscarte.
                      </p>
                      <div className="mt-2 flex items-center" data-id="element-542">
                        <div className="text-xs px-2 py-1 rounded-full bg-pink-100 text-pink-700" data-id="element-543">
                          En progreso
                        </div>
                      </div>
                      <div className="mt-3 bg-gray-50 p-3 rounded-md" data-id="element-544">
                        <h5 className="text-xs font-medium text-[#2C2C2C] mb-2" data-id="element-545">
                          Objetivos de esta fase:
                        </h5>
                        <ul className="text-xs text-gray-700 space-y-1" data-id="element-546">
                          <li className="flex items-start" data-id="element-547">
                            <div className="w-3 h-3 rounded-full border border-pink-500 mr-1 mt-0.5 flex items-center justify-center" data-id="element-548">
                              <div className="w-1.5 h-1.5 rounded-full bg-pink-500" data-id="element-549"></div>
                            </div>
                            <span data-id="element-550">
                              Lograr que inicie al menos una conversación
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-551">
                            <div className="w-3 h-3 rounded-full border border-pink-500 mr-1 mt-0.5 flex items-center justify-center" data-id="element-552">
                              <div className="w-1.5 h-1.5 rounded-full bg-pink-500" data-id="element-553"></div>
                            </div>
                            <span data-id="element-554">
                              Mantener conversaciones más largas que en la Fase
                              1
                            </span>
                          </li>
                          <li className="flex items-start" data-id="element-555">
                            <div className="w-3 h-3 rounded-full border border-gray-300 mr-1 mt-0.5" data-id="element-556"></div>
                            <span data-id="element-557">
                              Recibir al menos una pregunta personal de su parte
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-12" data-id="element-558">
                    <div className="absolute left-2 -translate-x-1/2 w-6 h-6 rounded-full bg-gray-300 text-white flex items-center justify-center text-xs" data-id="element-559">
                      3
                    </div>
                    <div data-id="element-560">
                      <h4 className="font-medium text-gray-500" data-id="element-561">
                        Fase 3: Conexión Emocional (Días 8-12)
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-562">
                        Profundiza la conexión con disparadores de
                        vulnerabilidad y gratitud. Crea momentos de intimidad
                        emocional y comparte visiones de futuro sin expectativas
                        explícitas.
                      </p>
                      <div className="mt-3 bg-gray-50 p-3 rounded-md" data-id="element-563">
                        <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-564">
                          Estrategia principal:
                        </h5>
                        <p className="text-xs text-gray-500" data-id="element-565">
                          Alternancia estratégica entre momentos de cercanía
                          emocional y espacio, creando un patrón de
                          intermitencia que active su sistema de apego. Enfócate
                          en validar su masculinidad y crear un espacio seguro
                          para su expresión emocional.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-12" data-id="element-566">
                    <div className="absolute left-2 -translate-x-1/2 w-6 h-6 rounded-full bg-gray-300 text-white flex items-center justify-center text-xs" data-id="element-567">
                      4
                    </div>
                    <div data-id="element-568">
                      <h4 className="font-medium text-gray-500" data-id="element-569">
                        Fase 4: Reencuentro Físico (Días 13-17)
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-570">
                        Planifica y ejecuta encuentros en persona con alta carga
                        emocional. Implementa técnicas de proximidad y contacto
                        estratégico que reactiven la atracción física.
                      </p>
                      <div className="mt-3 bg-gray-50 p-3 rounded-md" data-id="element-571">
                        <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-572">
                          Técnicas principales:
                        </h5>
                        <p className="text-xs text-gray-500" data-id="element-573">
                          Utilización del "efecto puente colgante" (la
                          excitación fisiológica de actividades compartidas se
                          atribuye inconscientemente a la atracción).
                          Implementación de patrones de contacto casual que
                          activen su memoria muscular de intimidad previa.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="relative pl-12" data-id="element-574">
                    <div className="absolute left-2 -translate-x-1/2 w-6 h-6 rounded-full bg-gray-300 text-white flex items-center justify-center text-xs" data-id="element-575">
                      5
                    </div>
                    <div data-id="element-576">
                      <h4 className="font-medium text-gray-500" data-id="element-577">
                        Fase 5: Consolidación (Días 18-21)
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-578">
                        Establece nuevos patrones de relación y comunicación.
                        Implementa los 7 pilares de obsesión duradera para
                        garantizar una reconexión profunda y sostenible.
                      </p>
                      <div className="mt-3 bg-gray-50 p-3 rounded-md" data-id="element-579">
                        <h5 className="text-xs font-medium text-gray-500 mb-2" data-id="element-580">
                          Los 7 Pilares de Obsesión Duradera:
                        </h5>
                        <ol className="text-xs text-gray-500 space-y-1 list-decimal pl-4" data-id="element-581">
                          <li data-id="element-582">Intermitencia emocional controlada</li>
                          <li data-id="element-583">Validación masculina estratégica</li>
                          <li data-id="element-584">Misterio sostenible</li>
                          <li data-id="element-585">Admiración auténtica</li>
                          <li data-id="element-586">Independencia atractiva</li>
                          <li data-id="element-587">Vulnerabilidad calibrada</li>
                          <li data-id="element-588">Proyección de futuro compartido</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-589">
              <h3 className="font-semibold text-[#9C27B0] mb-3" data-id="element-590">
                Acciones Prioritarias para Hoy
              </h3>
              <div className="space-y-3" data-id="element-591">
                <div className="flex items-start bg-pink-50 p-4 rounded-lg" data-id="element-592">
                  <div className="mr-3 mt-0.5" data-id="element-593">
                    <MessageSquareIcon size={18} className="text-pink-600" data-id="element-594" />
                  </div>
                  <div data-id="element-595">
                    <h4 className="font-medium text-[#2C2C2C]" data-id="element-596">
                      Enviar Disparador de Nostalgia #2
                    </h4>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-597">
                      Ideal para enviar entre 19:00-21:00h. Este mensaje evoca
                      recuerdos compartidos sin parecer desesperada. Encontrarás
                      el texto exacto en la Biblioteca de Mensajes.
                    </p>
                    <div className="mt-3 bg-white p-3 rounded border border-pink-100 text-sm" data-id="element-598">
                      <p className="italic text-gray-700" data-id="element-599">
                        "Pasé hoy por aquel [lugar específico] donde [memoria
                        compartida]. No pude evitar sonreír recordando ese día."
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-600">
                        Personaliza este mensaje con un recuerdo específico
                        donde él se sintió competente, protector o admirado.
                      </p>
                    </div>
                    <div className="mt-2" data-id="element-601">
                      <a href="/messages" className="text-xs font-medium text-pink-600 flex items-center" data-id="element-602">
                        Ver mensaje completo y variaciones
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-603">
                          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-604" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex items-start bg-blue-50 p-4 rounded-lg" data-id="element-605">
                  <div className="mr-3 mt-0.5" data-id="element-606">
                    <BookOpenIcon size={18} className="text-blue-600" data-id="element-607" />
                  </div>
                  <div data-id="element-608">
                    <h4 className="font-medium text-[#2C2C2C]" data-id="element-609">
                      Ejercicio de Desarrollo Personal
                    </h4>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-610">
                      Completa el ejercicio "Diario de Valoración" para
                      fortalecer tu autoestima. Este ejercicio es crucial para
                      proyectar confianza en tus interacciones con él.
                    </p>
                    <div className="mt-3 bg-white p-3 rounded border border-blue-100 text-sm" data-id="element-611">
                      <p className="font-medium text-gray-700" data-id="element-612">
                        Diario de Valoración - Instrucciones:
                      </p>
                      <ol className="text-xs text-gray-700 mt-1 space-y-1 list-decimal pl-4" data-id="element-613">
                        <li data-id="element-614">
                          Escribe 3 cualidades personales que posees y que son
                          valiosas en una relación
                        </li>
                        <li data-id="element-615">
                          Para cada cualidad, anota una situación reciente donde
                          la demostraste
                        </li>
                        <li data-id="element-616">
                          Reflexiona sobre cómo estas cualidades te hacen una
                          compañera valiosa independientemente de esta
                          reconciliación
                        </li>
                      </ol>
                    </div>
                    <div className="mt-2" data-id="element-617">
                      <a href="/personal-growth" className="text-xs font-medium text-blue-600 flex items-center" data-id="element-618">
                        Ver ejercicio completo
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-619">
                          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-620" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex items-start bg-purple-50 p-4 rounded-lg" data-id="element-621">
                  <div className="mr-3 mt-0.5" data-id="element-622">
                    <BrainIcon size={18} className="text-purple-600" data-id="element-623" />
                  </div>
                  <div data-id="element-624">
                    <h4 className="font-medium text-[#2C2C2C]" data-id="element-625">
                      Comprende su Psicología Masculina
                    </h4>
                    <p className="text-sm text-gray-700 mt-1" data-id="element-626">
                      Lee el análisis sobre el "Principio de Escasez" y cómo
                      afecta el comportamiento masculino. Entender este
                      principio te ayudará a mantener su interés de forma
                      sostenible.
                    </p>
                    <div className="mt-3 bg-white p-3 rounded border border-purple-100 text-sm" data-id="element-627">
                      <p className="font-medium text-gray-700" data-id="element-628">
                        El Principio de Escasez - Aplicación:
                      </p>
                      <p className="text-xs text-gray-700 mt-1" data-id="element-629">
                        Los hombres valoran más lo que perciben como escaso o
                        difícil de obtener. Cuando tu atención, tiempo y
                        disponibilidad emocional son ligeramente limitados (sin
                        juegos manipulativos), su cerebro atribuye mayor valor a
                        las interacciones contigo.
                      </p>
                    </div>
                    <div className="mt-2" data-id="element-630">
                      <a href="#" className="text-xs font-medium text-purple-600 flex items-center" data-id="element-631">
                        Ver biblioteca completa de psicología masculina
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-632">
                          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-633" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="max-w-4xl mx-auto" data-id="element-634">
      <header className="mb-8" data-id="element-635">
        <div className="text-center mb-4" data-id="element-636">
          <h1 className="text-3xl font-bold text-[#2C2C2C] mb-2" data-id="element-637">
            Bienvenida, {userName || 'Mujer Poderosa'}
          </h1>
          <p className="text-gray-600" data-id="element-638">
            Tu viaje de 21 días para reconquistar su corazón
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-4" data-id="element-639">
          <div className="flex overflow-x-auto space-x-2" data-id="element-640">
            <button onClick={() => setSelectedTab('resumen')} className={`px-4 py-2 rounded-md whitespace-nowrap ${selectedTab === 'resumen' ? 'bg-[#E91E63] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-641">
              Resumen
            </button>
            <button onClick={() => setSelectedTab('insights')} className={`px-4 py-2 rounded-md whitespace-nowrap ${selectedTab === 'insights' ? 'bg-[#E91E63] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-642">
              Análisis y Patrones
            </button>
            <button onClick={() => setSelectedTab('actividad')} className={`px-4 py-2 rounded-md whitespace-nowrap ${selectedTab === 'actividad' ? 'bg-[#E91E63] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-643">
              Registro de Actividad
            </button>
            <button onClick={() => setSelectedTab('plan')} className={`px-4 py-2 rounded-md whitespace-nowrap ${selectedTab === 'plan' ? 'bg-[#E91E63] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-644">
              Plan Personalizado
            </button>
          </div>
        </div>
      </header>
      {renderTabContent()}
    </div>;
};
export default Dashboard;